import { ArrayStringFilterOperator as OrderEditingFilterOperator } from "@redotech/redo-views-common/advanced-filters/array-string-filter";
import { FilterOperator } from "@redotech/redo-views-common/advanced-filters/generic-advanced-filter-data";
import { assertNever } from "@redotech/util/type";
import type {
  OrderEditingFilter,
  OrderEditingFilterName,
  OrderEditingRule,
  OrderEditingRuleMatch,
  OrderEditingTools,
} from "./order-editing-settings";

/**
 * The minimal slice of an order needed to evaluate order editing filters and
 * rules. Kept free of any database/server types so both the server and the
 * customer-facing clients can resolve the same configuration.
 */
export interface OrderEditingFilterableOrder {
  /** Order tags, already split into individual trimmed tags. */
  tags: string[];
  /** Shopify order name/number, e.g. "#1001". */
  orderName?: string;
}

/** Splits a Shopify tags value (comma-separated string or array) into tags. */
export function parseOrderTags(
  tags: string | string[] | null | undefined,
): string[] {
  if (!tags) {
    return [];
  }
  const raw = Array.isArray(tags) ? tags : tags.split(",");
  return raw.map((tag) => tag.trim()).filter((tag) => tag.length > 0);
}

function getOrderValuesForFilter(
  order: OrderEditingFilterableOrder,
  filterName: OrderEditingFilterName,
): string[] {
  // Compared by string value (not the enum) so this module stays free of a
  // runtime dependency on team.ts, which has a fragile circular init.
  switch (filterName as string) {
    case "order_tags":
      return order.tags;
    case "order_id":
      return order.orderName ? [order.orderName] : [];
    default:
      return [];
  }
}

export function orderMatchesFilter(
  order: OrderEditingFilterableOrder,
  filter: OrderEditingFilter,
): boolean {
  const orderValues = getOrderValuesForFilter(order, filter.name);
  const filterValues = filter.value ?? [];

  switch (filter.operator) {
    case OrderEditingFilterOperator.IS_EMPTY:
      return orderValues.length === 0;

    case OrderEditingFilterOperator.IS_NOT_EMPTY:
      return orderValues.length > 0;

    case OrderEditingFilterOperator.ANY_OF:
      return filterValues.some((filterVal) =>
        orderValues.some(
          (orderVal) => orderVal.toLowerCase() === filterVal.toLowerCase(),
        ),
      );

    case OrderEditingFilterOperator.ALL_OF:
      return filterValues.every((filterVal) =>
        orderValues.some(
          (orderVal) => orderVal.toLowerCase() === filterVal.toLowerCase(),
        ),
      );

    case OrderEditingFilterOperator.NONE_OF:
      return !filterValues.some((filterVal) =>
        orderValues.some(
          (orderVal) => orderVal.toLowerCase() === filterVal.toLowerCase(),
        ),
      );

    // Order tags/names are single keyword-ish values, so an exact phrase match
    // is whole-value equality — the same thing match_phrase does on a keyword.
    case OrderEditingFilterOperator.MATCHES_EXACTLY:
      if (filterValues.length === 0) {
        return true;
      }
      return orderValues.some((orderVal) =>
        filterValues.some(
          (filterVal) => orderVal.toLowerCase() === filterVal.toLowerCase(),
        ),
      );

    case OrderEditingFilterOperator.CONTAINS:
      if (filterValues.length === 0) {
        return true;
      }
      return orderValues.some((orderVal) =>
        filterValues.some((filterVal) =>
          orderVal.toLowerCase().includes(filterVal.toLowerCase()),
        ),
      );

    case OrderEditingFilterOperator.NOT_CONTAINS:
      if (filterValues.length === 0) {
        return true;
      }
      return !orderValues.some((orderVal) =>
        filterValues.some((filterVal) =>
          orderVal.toLowerCase().includes(filterVal.toLowerCase()),
        ),
      );

    case OrderEditingFilterOperator.STARTS_WITH:
      if (filterValues.length === 0) {
        return true;
      }
      return orderValues.some((orderVal) =>
        filterValues.some((filterVal) =>
          orderVal.toLowerCase().startsWith(filterVal.toLowerCase()),
        ),
      );

    case OrderEditingFilterOperator.DOES_NOT_START_WITH:
      if (filterValues.length === 0) {
        return true;
      }
      return !orderValues.some((orderVal) =>
        filterValues.some((filterVal) =>
          orderVal.toLowerCase().startsWith(filterVal.toLowerCase()),
        ),
      );

    default:
      assertNever(filter.operator);
  }
}

/**
 * Legacy global filter gate: an order passes when it matches ALL filters.
 * Returns true when there are no filters configured.
 */
export function orderMatchesAllFilters(
  order: OrderEditingFilterableOrder,
  filters: OrderEditingFilter[] | null | undefined,
): boolean {
  if (!filters || filters.length === 0) {
    return true;
  }
  return filters.every((filter) => orderMatchesFilter(order, filter));
}

/**
 * Evaluates a rule's match condition against an order. Filters are combined
 * with the group's single boolean operator:
 * - AND: every filter must match
 * - OR: at least one filter must match
 *
 * An empty filter list always matches, which is how the catch-all default
 * rule applies its configuration to every order.
 */
export function orderMatchesGroup(
  order: OrderEditingFilterableOrder,
  match: OrderEditingRuleMatch | null | undefined,
): boolean {
  const filters = match?.filters ?? [];
  if (filters.length === 0) {
    return true;
  }

  if (match?.operator === FilterOperator.OR) {
    return filters.some((filter) => orderMatchesFilter(order, filter));
  }

  return filters.every((filter) => orderMatchesFilter(order, filter));
}

export type ResolvedOrderEditingConfig =
  | { tools: OrderEditingTools }
  | { disabledReason: string };

/**
 * The slice of order editing settings needed to resolve a config. Kept narrow
 * (and tolerant of `null`) so the database-hydrated settings type assigns here
 * without conflicting on unrelated fields like `holds`.
 */
export interface OrderEditingConfigSource {
  rules?: OrderEditingRule[] | null;
  tools?: OrderEditingTools | null;
  filters?: OrderEditingFilter[] | null;
}

/**
 * Resolves which set of order editing tools (enabled actions + time windows)
 * applies to an order.
 *
 * When `rules` are configured, the first rule whose match passes wins
 * (first-match-wins). A rule with no filters always matches and acts as the
 * catch-all default, so a well-formed rule list ends with one. If no rule
 * matches, editing is disabled.
 *
 * When no rules are configured, the legacy behavior applies: the global
 * `filters` gate all editing and the global `tools` supply the windows.
 */
export function resolveOrderEditingConfig(
  order: OrderEditingFilterableOrder,
  settings: OrderEditingConfigSource | null | undefined,
): ResolvedOrderEditingConfig {
  const rules = settings?.rules;
  if (rules && rules.length > 0) {
    for (const rule of rules) {
      if (orderMatchesGroup(order, rule.match)) {
        return { tools: rule.tools };
      }
    }
    return { disabledReason: "Order does not match any order editing rule" };
  }

  // Legacy fallback: no rules configured.
  if (!settings?.tools) {
    return { disabledReason: "Order editing is not enabled for this team" };
  }
  if (!orderMatchesAllFilters(order, settings.filters)) {
    return { disabledReason: "Order does not match the configured filters" };
  }
  return { tools: settings.tools };
}

/** Which actions are enabled anywhere in the config, ignoring order tags. */
export interface AggregatedOrderEditingTools {
  enabled: boolean;
  cancelOrder: boolean;
  changeAddress: boolean;
  addOrderDiscount: boolean;
  addOrderGiftNote: boolean;
  updateOrder: {
    enabled: boolean;
    addItem: boolean;
    removeItem: boolean;
    changeVariant: boolean;
  };
}

/** Action enabled when ANY rule enables it; no rules falls back to global `tools`. */
export function aggregateEnabledOrderEditingTools(
  settings: OrderEditingConfigSource | null | undefined,
): AggregatedOrderEditingTools {
  const rules = settings?.rules;
  const toolSets: OrderEditingTools[] =
    rules && rules.length > 0
      ? rules.map((rule) => rule.tools)
      : settings?.tools
        ? [settings.tools]
        : [];

  // updateOrder sub-actions only count from rules where updateOrder itself is
  // enabled, since add/remove/changeVariant are meaningless otherwise.
  const updateOrderSets = toolSets.filter((t) => t.updateOrder.enabled);

  return {
    enabled: toolSets.length > 0,
    cancelOrder: toolSets.some((t) => t.cancelOrder.enabled),
    changeAddress: toolSets.some((t) => t.changeAddress.enabled),
    addOrderDiscount: toolSets.some((t) => t.addOrderDiscount.enabled),
    addOrderGiftNote: toolSets.some((t) => t.addOrderGiftNote.enabled),
    updateOrder: {
      enabled: updateOrderSets.length > 0,
      addItem: updateOrderSets.some((t) => t.updateOrder.addItem),
      removeItem: updateOrderSets.some((t) => t.updateOrder.removeItem),
      changeVariant: updateOrderSets.some((t) => t.updateOrder.changeVariant),
    },
  };
}

/** True when any action is enabled in any rule, or in legacy tools when no rules exist. */
export function anyOrderEditingActionEnabled(
  settings: OrderEditingConfigSource | null | undefined,
): boolean {
  const aggregated = aggregateEnabledOrderEditingTools(settings);
  return (
    aggregated.cancelOrder ||
    aggregated.changeAddress ||
    aggregated.addOrderDiscount ||
    aggregated.addOrderGiftNote ||
    aggregated.updateOrder.enabled
  );
}
